import React, { useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { Page, LayoutSingleColumn, NamedLink } from '../../components';
import TopbarContainer from '../TopbarContainer/TopbarContainer';
import FooterContainer from '../FooterContainer/FooterContainer';
import { isScrollingDisabled } from '../../ducks/ui.duck';
import { submitEnquiry } from '../../util/api';
import fields from '../../config/enquiryFields.json';
import css from './EnquiryPage.module.css';

export const EnquiryForm = () => {
  const [pending, setPending] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState('');
  const submitting = useRef(false);

  const handleSubmit = async event => {
    event.preventDefault();
    if (submitting.current) return;
    const data = new FormData(event.currentTarget);
    const values = fields.reduce((result, field) => {
      result[field.name] = (data.get(field.name) || '').trim();
      return result;
    }, {});
    values.website = data.get('website') || '';
    submitting.current = true;
    setPending(true);
    setError('');
    try {
      await submitEnquiry(values);
      setSent(true);
    } catch (e) {
      setError(e.message || 'We could not send your enquiry. Please try again.');
    } finally {
      submitting.current = false;
      setPending(false);
    }
  };

  if (sent) {
    return (
      <div className={css.success} role="status">
        <h2>Thank you! We will get back to you shortly.</h2>
        <NamedLink name="LandingPage">Return to the homepage</NamedLink>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} className={css.form} aria-busy={pending}>
      <p className={css.requiredNote}>Fields marked * are required.</p>
      {fields.map((field, index) => {
        const id = `enquiry-${field.name}`;
        const inputProps = {
          id,
          name: field.name,
          required: field.required,
          maxLength: field.maxLength,
          'aria-describedby': field.description ? `${id}-description` : undefined,
        };
        return (
          <div className={css.field} key={field.name}>
            <label htmlFor={id}>
              <span className={css.number}>{index + 1}.</span> {field.label}
              {field.required ? ' *' : ''}
            </label>
            {field.description && <p id={`${id}-description`}>{field.description}</p>}
            {field.choices.length ? (
              <select {...inputProps} defaultValue="">
                <option value="">Select an option</option>
                {field.choices.map(choice => (
                  <option key={choice}>{choice}</option>
                ))}
              </select>
            ) : field.type === 'long_text' ? (
              <textarea {...inputProps} rows={4} />
            ) : (
              <input
                {...inputProps}
                type={
                  field.type === 'email' ? 'email' : field.type === 'phone_number' ? 'tel' : 'text'
                }
                autoComplete={{ name: 'name', email: 'email', phone: 'tel' }[field.name]}
              />
            )}
          </div>
        );
      })}
      <div className={css.honeypot} aria-hidden="true">
        <label htmlFor="enquiry-website">Leave this field empty</label>
        <input id="enquiry-website" name="website" tabIndex={-1} autoComplete="off" />
      </div>
      {error && (
        <p className={css.error} role="alert">
          {error}
        </p>
      )}
      <button className={css.submit} type="submit" disabled={pending}>
        {pending ? 'Sending your enquiry…' : 'Send enquiry'}
      </button>
    </form>
  );
};

const EnquiryPage = () => {
  const scrollingDisabled = useSelector(isScrollingDisabled);
  return (
    <Page title="Make an enquiry | Suites and Villas" scrollingDisabled={scrollingDisabled}>
      <LayoutSingleColumn topbar={<TopbarContainer />} footer={<FooterContainer />}>
        <section className={css.root}>
          <header className={css.intro}>
            <p className={css.eyebrow}>MAKE AN ENQUIRY</p>
            <h1>Find your perfect stay.</h1>
            <p>Take a few minutes to help us find your perfect stay.</p>
            <p className={css.minimum}>Rental enquiries only · From $500 / £400 / €400 per night</p>
          </header>
          <EnquiryForm />
        </section>
      </LayoutSingleColumn>
    </Page>
  );
};

export default EnquiryPage;
